import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAllCasinoStudiosStart } from '../../store/redux-slices/superAdminCasinoManagement'

/**
 * Custom hook for filtering casino studios based on provider and aggregator selection
 * @param {Object} params - Hook parameters
 * @param {boolean} params.isTenantRoute - Flag indicating if the current route is tenant-specific
 * @param {string} params.selectedProvider - ID of the selected casino provider
 * @param {string} params.selectedAggregator - ID of the selected game aggregator
 * @returns {Object} Object containing studio filter state and methods
 */
const useStudioFilter = ({ isTenantRoute, selectedProvider, selectedAggregator }) => {
  const dispatch = useDispatch()
  const { casinoStudios = [] } = useSelector((state) => state?.superAdminCasino)
  const { allProviders = [] } = useSelector((state) => isTenantRoute ? state?.tenantCasino : state?.superAdminCasino)
  const [selectedStudio, setSelectedStudio] = useState('')

  /**
   * Memoized function to filter studios based on selected provider or aggregator
   * @returns {Array} Filtered array of casino studios
   */
  const filteredStudios = useMemo(() => {
    if (!Array.isArray(casinoStudios)) {
      return []
    }

    // If a provider is selected, only return studios for that provider
    if (selectedProvider) {
      return casinoStudios?.filter((studio) => String(studio.casinoProviderId) === selectedProvider) || []
    }

    // If an aggregator is selected, only return studios for that aggregator
    if (selectedAggregator) {
      return casinoStudios?.filter((studio) => String(studio.masterGameAggregatorId) === String(selectedAggregator)) || []
    }

    return casinoStudios || []
  }, [allProviders, casinoStudios, selectedAggregator, selectedProvider])

  // Fetch studios on an initial load
  useEffect(() => {
    dispatch(
      getAllCasinoStudiosStart({
        isTenant: true,
        pageNo: '',
        limit: '',
        search: ''
      })
    )
  }, [])

  // Reset a selected studio when the provider changes
  useEffect(() => {
    setSelectedStudio('')
  }, [selectedProvider])

  /** @type {{selectedStudio: string, setSelectedStudio: Function, filteredStudios: Array}} */
  return {
    selectedStudio,
    setSelectedStudio,
    filteredStudios
  }
}

export default useStudioFilter
